<script lang="ts">
import { defineComponent, PropType, ref, computed, watch } from 'vue'
import InputForm from '@/components/form/InputForm.vue'
import { buildRetryQueries, extractCorporateNumber, isLatinLike, isPhoneLike, normalizeWidth } from '@/models/companySearchQuery'
import type { RetryQuery } from '@/models/companySearchQuery'
import { isValidNumber } from '@/utilities/validator'

/**
 * 出し分ける案内の種類。cause に入力値から分かる phone_like / latin_like を加えたもの
 */
type SearchGuideCategory = CompanySearchGuideCause | 'phone_like' | 'latin_like'

const CORPORATE_NUMBER_LENGTH = 13 as const
const NTA_CORPORATE_NUMBER_SITE_URL = 'https://www.houjin-bangou.nta.go.jp/' as const

/**
 * 案内の種類ごとの表示内容
 *
 * @description
 *  hasRetryChip は「書き方を変えて探し直す余地があるか」。Record にしているため、
 *  種別を追加すると文言とチップ表示の両方を必ず決めることになる
 */
const GUIDES: Record<SearchGuideCategory, { title: (query: string) => string, body: string, hasRetryChip: boolean }> = {
  no_results: {
    title: query => `「${query}」に一致する会社が見つかりませんでした`,
    body: '略称や旧社名、支店名で登録されていない場合があります。書き方を変えるか、法人番号で探せます。',
    hasRetryChip: true,
  },
  phone_like: {
    title: () => '電話番号のようです',
    body: '勤務先名を入力してください。法人番号で探す場合は13桁の数字を下の欄に入力してください。',
    // 電話番号を入れているので、書き方を変えても会社は見つからない
    hasRetryChip: false,
  },
  latin_like: {
    title: query => `「${query}」に一致する会社が見つかりませんでした`,
    body: '英字の社名でも、登記はカタカナのことがあります。「BizHint」なら「ビズヒント」のように、カタカナでもお試しください。',
    hasRetryChip: true,
  },
  api_error: {
    title: () => '会社検索サービスに接続できませんでした',
    body: '時間をおいて入力し直すか、法人番号で探せます。',
    // 検索自体が失敗しているので、クエリを変えても解決しない
    hasRetryChip: false,
  },
  user_initiated: {
    title: () => '別の探し方で探す',
    body: '書き方を変えるか、法人番号で探せます。候補一覧に無い場合は、そのまま登録できます。',
    hasRetryChip: true,
  },
}

export default defineComponent({
  name: 'CompanySearchGuidePanel',

  components: {
    InputForm,
  },

  props: {
    /**
     * 会社名欄の入力値（見出しと登録ボタンの文言に使う）
     */
    query: {
      type: String,
      required: true,
    },

    /**
     * パネルを開いた原因。入力値から分かることは親に持たせず、ここから自分で導出する
     */
    cause: {
      type: String as PropType<CompanySearchGuideCause>,
      required: true,
    },

    /**
     * すでに試した再検索クエリ（disabled 表示にする）
     */
    triedQueries: {
      type: Array as PropType<string[]>,
      required: true,
    },

    /**
     * 法人番号照会の状態（親が API 結果で更新する）
     */
    corporateNumberLookupStatus: {
      type: String as PropType<CorporateNumberLookupStatus>,
      required: true,
    },
  },

  emits: ['click:retryQuery', 'fill:corporateNumber', 'click:notApplicable'],

  setup(props, { emit }) {
    const corporateNumberInput = ref('')
    const lookedUpCorporateNumber = ref('')

    /**
     * 出し分ける案内の種類。入力値だけで決まること（電話番号・英字）はここで判定する
     */
    const searchGuideCategory = computed<SearchGuideCategory>(() => {
      if (props.cause === 'api_error') return 'api_error'
      if (isPhoneLike(props.query)) return 'phone_like'
      if (props.cause === 'user_initiated') return 'user_initiated'
      if (isLatinLike(props.query)) return 'latin_like'

      return 'no_results'
    })

    /**
     * 再検索チップ。法人番号を入力しているときは書き方を変えても意味がないため出さない
     */
    const retryQueries = computed<RetryQuery[]>(() => {
      if (extractCorporateNumber(props.query)) return []

      return buildRetryQueries(props.query)
    })

    const guideMessage = computed<{ title: string, body: string }>(() => {
      return {
        title: GUIDES[searchGuideCategory.value].title(props.query),
        body: GUIDES[searchGuideCategory.value].body,
      }
    })

    const isShowRetryQueries = computed<boolean>(() => (
      GUIDES[searchGuideCategory.value].hasRetryChip
      && retryQueries.value.length > 0
    ))

    /**
     * 入力された法人番号から区切り（空白・ハイフン）を除き、全角を半角に揃えた値
     *
     * @description 照会に使う extractCorporateNumber と同じ正規化。入力形式の判定と桁数表示の基準を揃える
     */
    const normalizedCorporateNumber = computed<string>(() => normalizeWidth(corporateNumberInput.value).replace(/[\s\-‐−]/g, ''))

    const corporateNumberDigitCount = computed<number>(() => normalizedCorporateNumber.value.replace(/\D/g, '').length)

    /**
     * 法人番号欄のメッセージをエラー表示にするか
     *
     * @description 入力形式の誤りも、照会して見つからなかった場合も、ユーザーの操作が必要な点は同じ
     */
    const isLookupError = computed<boolean>(() => {
      if (props.corporateNumberLookupStatus === 'not_found') return true
      // 不通はユーザーの落ち度ではないが、時間をおくか候補外として登録するかの操作が要る点は同じ（#8982）
      if (props.corporateNumberLookupStatus === 'unavailable') return true
      if (!corporateNumberInput.value.length) return false

      return !isValidNumber(normalizedCorporateNumber.value)
    })

    const lookupMessage = computed<string>(() => {
      // 照会そのものができていないので、番号を入れ直しても解決しない。入力の有無に関わらず出す（#8982）
      if (props.corporateNumberLookupStatus === 'unavailable') return '会社情報の照会が一時的にご利用いただけません'
      if (!corporateNumberInput.value.length) return ''
      // 判定は照会に使う値（extractCorporateNumber と同じ正規化）に揃える。
      // 区切りや全角数字は照会側が受理するため、ここで弾くと「照会は通るのに入力形式エラーが出る」ことになる
      if (!isValidNumber(normalizedCorporateNumber.value)) return '法人番号は13桁の数字で入力してください'
      if (props.corporateNumberLookupStatus === 'loading') return '確認しています…'
      if (props.corporateNumberLookupStatus === 'not_found') return 'この番号の会社は見つかりませんでした'
      return ''
    })

    // 不通は時間をおけば解決するので、同じ番号での再照会の抑止を解く。
    // 解かないと「時間をおいて」と案内しておきながら同じ番号を入れ直せない（#8982）
    watch(() => props.corporateNumberLookupStatus, (status) => {
      if (status !== 'unavailable') return

      lookedUpCorporateNumber.value = ''
    })

    // 13 桁そろった瞬間に照会する。同じ番号での再照会は抑止する
    watch(corporateNumberInput, (newValue) => {
      const corporateNumber = extractCorporateNumber(newValue)
      if (!corporateNumber || corporateNumber === lookedUpCorporateNumber.value) return

      lookedUpCorporateNumber.value = corporateNumber
      emit('fill:corporateNumber', corporateNumber)
    })

    const isTried = (query: string): boolean => props.triedQueries.includes(query)

    return {
      // constants
      NTA_CORPORATE_NUMBER_SITE_URL,
      CORPORATE_NUMBER_LENGTH,

      // data
      corporateNumberInput,
      corporateNumberDigitCount,

      // computed
      guideMessage,
      retryQueries,
      isShowRetryQueries,
      isLookupError,
      lookupMessage,

      // methods
      isTried,
    }
  }
})
</script>

<template>
  <div class="l-unit__companySearchGuide">
    <div class="l-unit__companySearchGuide--header">
      <p class="p u-text__small u-text__bold">
        {{ guideMessage.title }}
      </p>

      <div class="o-note u-text__x-small u-margin__smaller">
        <p class="o-note--item">
          {{ guideMessage.body }}
        </p>
      <!-- /.o-note --></div>
    <!-- /.l-unit__companySearchGuide header --></div>

    <div class="l-unit__companySearchGuide--description">
      <div class="l-rows is-border">
        <div
          v-if="isShowRetryQueries"
          class="l-rows--row">
          <dl class="dl">
            <dt class="dt u-text__bold u-text__x-small__fixed">
              別の書き方で探す
            </dt>

            <dd class="dd">
              <div class="o-list is-horizontal">
                <ul class="o-list--items">
                  <li
                    v-for="retry in retryQueries"
                    :key="retry.query"
                    class="o-list--item">
                    <div class="o-action__retry">
                      <button
                        class="o-action__retry--button"
                        :disabled="isTried(retry.query)"
                        @click.prevent="$emit('click:retryQuery', retry.query, retry.kind)">
                        {{ retry.label }}
                      </button>
                    <!-- /.o-action__retry --></div>
                  </li>
                </ul>
              <!-- /.o-list --></div>
            </dd>
          </dl>
        <!-- /.l-rows row --></div>

        <div class="l-rows--row">
          <InputForm
            v-model="corporateNumberInput"
            header="法人番号で探す（13桁）"
            type="tel"
            inputmode="numeric"
            placeholder="例：6011001149027"
            :maxlength="20"
            size="small" />

          <!--
            ライブリージョンは内容が変わる前から DOM に存在している必要があるため、
            この div 自体は出し入れせず、中の p だけを差し替える
          -->
          <div
            class="o-label__lookupMessage"
            :data-error="isLookupError"
            aria-live="polite">
            <p
              v-if="lookupMessage"
              class="o-label__lookupMessage--item is-status">
              {{ lookupMessage }}
            </p>

            <p
              v-else-if="!!corporateNumberDigitCount"
              class="o-label__lookupMessage--item is-count">
              {{ corporateNumberDigitCount }}/{{ CORPORATE_NUMBER_LENGTH }}桁
            </p>
          <!-- /.o-label__lookupMessage --></div>

          <div class="o-note__general u-text__x-small u-margin__smaller">
            <p class="o-note__general--item">法人番号は
              <a
                class="a u-link__blank"
                :href="NTA_CORPORATE_NUMBER_SITE_URL"
                target="_blank"
                rel="noopener noreferrer">
                国税庁 法人番号公表サイト
              </a>
              で会社名から調べられます
            </p>
          <!-- /.o-note__general --></div>
        <!-- /.l-rows row --></div>

        <div class="l-rows--row">
          <dl class="dl">
            <dt class="dt u-text__bold u-text__x-small__fixed">
              それでも見つからない場合
            </dt>

            <dd class="dd">
              <div class="o-action__registerUnverified">
                <button
                  class="o-action__registerUnverified--item"
                  @click.prevent="$emit('click:notApplicable')">
                  「{{ query }}」を候補に無い会社として登録する
                </button>
              <!-- /.o-action__registerUnverified --></div>

              <div class="o-note__general u-text__x-small u-margin__smaller">
                <p class="o-note__general--item">業種に合ったおすすめコンテンツは届きません</p>
              <!-- /.o-note__general --></div>
            </dd>
          </dl>
        <!-- /.l-rows row --></div>
      <!-- /.l-rows --></div>
    <!-- /.l-unit__companySearchGuide description --></div>
  <!-- /.l-unit__companySearchGuide --></div>
</template>

<style lang="sass" scoped>
.l-unit__companySearchGuide
  padding: 1em
  background-color: $bg-xx-light-cyan
  border: 1px solid $border-xx-light-cyan
  border-radius: $border-radius

  & &--description
    margin-top: 1.25em

.o-label__lookupMessage
  $root: &
  margin-top: 0.5em

  &:first-child
    margin-top: 0

  & &--item
    color: $font-gray
    font-size: $font-xx-small

  &[data-error="true"]
    #{$root}--item
      color: $font-error

.o-action__retry
  & &--button
    display: inline-block
    font-size: $font-x-small
    text-align: left
    padding: 0.5em 1em
    background-color: $bg-white
    border: 1px solid $border-xx-light-cyan
    border-radius: $border-radius
    +transition

    &:disabled
      color: $font-x-light-gray
      background-color: $bg-x-light-gray
      border-color: $border-x-light-gray

    +mouseover
      &:not(:disabled)
        background-color: $bg-x-light-cyan

.o-action__registerUnverified
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--item
    width: 100%
    display: block
    color: $font-gray
    font-size: $font-x-small
    text-align: center
    padding: 0.75em 1em
    background-color: $bg-white
    border: 1px solid $border-x-light-gray
    border-radius: $border-radius
    +transition

    +mouseover
      background-color: $bg-x-light-gray
</style>
